import { useMemo, useState } from 'react';
import { Mic, Subtitles, X, Plus } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  IconButton,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Checkbox,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import type { AdditionalTrack, AdditionalTrackType, JobFormData } from './use-job-create-form';
import { getStorageLabel } from './use-job-create-form';
import { SelectableCard } from './SelectableCard';
import styles from './JobCreateSourceSection.module.css';
import tileStyles from './SelectableTile.module.css';

const PROTOCOL_OPTIONS = [
  { value: 'ftp', label: 'FTP' },
  { value: 'http', label: 'HTTP' },
  { value: 'sftp', label: 'SFTP' },
  { value: 'webdav', label: 'WebDAV' },
  { value: 's3', label: 'AWS S3 / OSS' },
];

const LANGUAGE_OPTIONS = [
  { value: 'eng', label: 'English' },
  { value: 'chi', label: 'Chinese' },
  { value: 'spa', label: 'Spanish' },
  { value: 'may', label: 'Malay' },
  { value: 'tha', label: 'Thai' },
  { value: 'ind', label: 'Indonesian' },
  { value: 'fil', label: 'Filipino' },
  { value: 'vie', label: 'Vietnamese' },
  { value: 'bur', label: 'Burmese' },
  { value: 'ara', label: 'Arabic' },
  { value: 'mul', label: 'Multiple' },
];

interface Props {
  form: JobFormData;
  currentStorages: Record<string, unknown>[];
  selectedStorage: Record<string, unknown> | undefined;
  updateSource: (field: string, value: string) => void;
  updateMd5sum: (v: string) => void;
  addAdditionalTrack: (track: AdditionalTrack) => void;
  removeAdditionalTrack: (index: number) => void;
  fieldErrors?: { sourcePath?: string };
}

export function JobCreateSourceSection({
  form,
  currentStorages,
  selectedStorage,
  updateSource,
  updateMd5sum,
  addAdditionalTrack,
  removeAdditionalTrack,
  fieldErrors,
}: Props) {
  const [trackType, setTrackType] = useState<AdditionalTrackType>('subtitle');
  const [trackLang, setTrackLang] = useState('eng');
  const [trackPath, setTrackPath] = useState('');

  const expectedExt = trackType === 'audio' ? '.mp3 or .aac' : '.srt';
  const trackPathValid = useMemo(() => {
    const p = trackPath.trim().toLowerCase();
    if (!p) return false;
    return trackType === 'audio'
      ? p.endsWith('.mp3') || p.endsWith('.aac')
      : p.endsWith('.srt');
  }, [trackPath, trackType]);

  const onAddTrack = () => {
    const fp = trackPath.trim();
    if (!fp || !trackPathValid) return;
    addAdditionalTrack({ type: trackType, language: trackLang, file_path: fp });
    setTrackPath('');
  };

  return (
    <Section title="Source Location" style={{ marginBottom: 0 }}>
      <Card flat className={styles.cardInner}>
        <p className={styles.cardLabel}>Pull from</p>
        <div className={styles.protocolRow}>
          {PROTOCOL_OPTIONS.map((p) => {
            const active = form.source.protocol === p.value;
            return (
              <SelectableCard
                key={p.value}
                selected={active}
                className={`${tileStyles.tile} ${styles.protocolChip} ${active ? `${tileStyles.tileActive} ${styles.protocolChipActive}` : ''}`}
                onSelect={() => updateSource('protocol', p.value)}
              >
                <Checkbox
                  checked={active}
                  onCheckedChange={() => updateSource('protocol', p.value)}
                  aria-hidden
                  tabIndex={-1}
                  style={{ pointerEvents: 'none' }}
                />
                <span>{p.label}</span>
              </SelectableCard>
            );
          })}
        </div>

        {form.source.protocol && (
          <>
            <div className={styles.section}>
              <p className={styles.fieldLabel}>Source Storage</p>
              {currentStorages.length === 0 ? (
                <Alert tone="warning" title="No storages available">
                  No {form.source.protocol.toUpperCase()} storages configured. Add one under Output Storage settings.
                </Alert>
              ) : (
                <div className={styles.storageList}>
                  {currentStorages.map((s) => {
                    const val = String(s?.id ?? s?.name ?? '');
                    const isSelected = form.source.storage === val;
                    return (
                      <SelectableCard
                        key={val}
                        selected={isSelected}
                        className={`${tileStyles.tile} ${styles.storageCard} ${isSelected ? tileStyles.tileActive : ''}`}
                        onSelect={() => updateSource('storage', val)}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => updateSource('storage', val)}
                          aria-hidden
                          tabIndex={-1}
                          style={{ pointerEvents: 'none', flexShrink: 0 }}
                        />
                        <div>
                          <p className={styles.storageName}>{String(s?.name ?? val)}</p>
                          <p className={styles.storageUrl}>{getStorageLabel(s, form.source.protocol)}</p>
                        </div>
                      </SelectableCard>
                    );
                  })}
                </div>
              )}
              {selectedStorage && (
                <p className={styles.selectedHint}>
                  Selected: {getStorageLabel(selectedStorage, form.source.protocol)}
                </p>
              )}
            </div>

            <div className={styles.section}>
              <Field label="File Path" required error={fieldErrors?.sourcePath}>
                {(p) => (
                  <Input
                    {...p}
                    placeholder="/videos/sample.mp4"
                    value={form.source.path}
                    invalid={Boolean(fieldErrors?.sourcePath)}
                    onChange={(e) => updateSource('path', e.target.value)}
                  />
                )}
              </Field>
              <p className={styles.hint}>Path to the VOD file on the selected storage.</p>
            </div>

            <div className={styles.section}>
              <Field label="MD5 Checksum (optional)">
                {(p) => (
                  <Input
                    {...p}
                    placeholder="e.g. ebd123ebd123ebd123ebd123ebd123ab"
                    value={form.md5sum}
                    onChange={(e) => updateMd5sum(e.target.value)}
                  />
                )}
              </Field>
            </div>

            <div className={styles.section}>
              <p className={styles.fieldLabel}>Additional Audio / Subtitle Tracks</p>
              <div className={styles.trackRow}>
                <div>
                  <label htmlFor="track-type-select" className={styles.subLabel}>Type</label>
                  <Select value={trackType} onValueChange={(v) => setTrackType(v as AdditionalTrackType)}>
                    <SelectTrigger id="track-type-select" size="sm" className={styles.trackTypeSelect}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="subtitle">Subtitle</SelectItem>
                      <SelectItem value="audio">Audio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label htmlFor="track-lang-select" className={styles.subLabel}>Language</label>
                  <Select value={trackLang} onValueChange={setTrackLang}>
                    <SelectTrigger id="track-lang-select" size="sm" className={styles.trackLangSelect}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LANGUAGE_OPTIONS.map((l) => (
                        <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div style={{ flex: 1 }}>
                  <label htmlFor="track-path-input" className={styles.subLabel}>File Path</label>
                  <Input
                    id="track-path-input"
                    inputSize="sm"
                    placeholder={trackType === 'audio' ? 'track.mp3' : 'subtitle.srt'}
                    value={trackPath}
                    onChange={(e) => setTrackPath(e.target.value)}
                    invalid={Boolean(trackPath.trim()) && !trackPathValid}
                  />
                </div>
                <div style={{ alignSelf: 'flex-end' }}>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!trackPathValid}
                    onClick={onAddTrack}
                    startIcon={<Plus size={14} />}
                  >
                    Add track
                  </Button>
                </div>
              </div>
              <p className={styles.hint}>
                {Boolean(trackPath.trim()) && !trackPathValid
                  ? `Invalid extension. Required: ${expectedExt}`
                  : `Required extension: ${expectedExt}`}
              </p>

              {form.source.additionalTracks.length > 0 && (
                <div className={styles.trackList}>
                  {form.source.additionalTracks.map((t, i) => (
                    <div key={`${t.type}-${t.language}-${i}`} className={styles.trackItem}>
                      <span className={styles.trackIcon}>
                        {t.type === 'audio' ? <Mic size={14} /> : <Subtitles size={14} />}
                      </span>
                      <span className={styles.trackTypeLabel}>{t.type === 'audio' ? 'Audio' : 'Subtitle'}</span>
                      <span className={styles.trackLang}>{t.language}</span>
                      <span className={styles.trackPath} title={t.file_path}>{t.file_path}</span>
                      <IconButton
                        aria-label="Remove track"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAdditionalTrack(i)}
                      >
                        <X size={14} />
                      </IconButton>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </Card>
    </Section>
  );
}
